import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const navItems = [
  { en: "Home", ja: "ホーム", path: "/" },
  { en: "About", ja: "私たちについて", path: "/about" },
  { en: "Talent", ja: "所属タレント", path: "/talent" },
  { en: "Talent Management", ja: "タレントマネジメント事業", path: "/entertainment" },
  { en: "Farm", ja: "ファーム事業", path: "/farm" },
  { en: "EC site", ja: "ネットショップ", path: "/ec", external: "https://olcompany.base.shop/" },
  { en: "Company", ja: "会社概要", path: "/company" },
  { en: "News", ja: "ニュース", path: "/news" },
  { en: "Contact", ja: "お問い合わせ", path: "/contact" },
];

interface SidebarProps {
  footerVisible?: boolean;
}

export default function Sidebar({ footerVisible = false }: SidebarProps) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={`hidden lg:block fixed top-0 left-0 h-full w-[240px] z-50 transition-all duration-500 ${
          footerVisible
            ? "bg-[#18181b]"
            : "bg-transparent"
        }`}
      >
        <aside className={`flex flex-col pt-3 pb-6 px-6 bg-[#f0efef] ${
          footerVisible ? "h-[calc(100%-30px)] rounded-br-[32px]" : "h-full"
        }`}>
          {/* Logo */}
          <Link to="/" className="flex flex-col items-start mb-6 shrink-0">
            <img
              src="https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/926915bae22d08966848f6c0c2385dc6.webp"
              alt="OL Company"
              className="h-[90px] w-auto object-contain object-left"
            />
          </Link>

          {/* Nav */}
          <nav className="flex flex-col gap-0 flex-1">
            {navItems.map((item) => {
              const isActive =
                item.path === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.path);

              if (item.external) {
                return (
                  <a
                    key={item.en}
                    href={item.external}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col py-3 border-b border-gray-100 cursor-pointer"
                  >
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors ${
                          isActive ? "bg-black" : "bg-transparent group-hover:bg-gray-300"
                        }`}
                      />
                      <span className="text-[22px] font-bold text-gray-800 group-hover:text-black transition-colors leading-tight">
                        {item.en}
                      </span>
                    </div>
                    <span className="text-[11px] text-gray-400 mt-1 pl-3">{item.ja}</span>
                  </a>
                );
              }

              return (
                <Link
                  key={item.en}
                  to={item.path}
                  className="group flex flex-col py-3 border-b border-gray-100 cursor-pointer"
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors ${
                        isActive ? "bg-black" : "bg-transparent group-hover:bg-gray-300"
                      }`}
                    />
                    <span
                      className={`text-[22px] font-bold leading-tight transition-colors ${
                        isActive ? "text-black" : "text-gray-600 group-hover:text-black"
                      }`}
                    >
                      {item.en}
                    </span>
                  </div>
                  <span className="text-[11px] text-gray-400 mt-1 pl-3">{item.ja}</span>
                </Link>
              );
            })}
          </nav>

          {/* SNS */}
          <div className="flex items-center gap-4 mt-auto pt-4 shrink-0">
            <a
              href="https://www.instagram.com/olcompany_inc/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 flex items-center justify-center text-gray-800 hover:text-black transition-colors cursor-pointer"
            >
              <i className="ri-instagram-line text-2xl" />
            </a>
            <a
              href="https://www.youtube.com/@O.L.C"
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 flex items-center justify-center text-gray-800 hover:text-black transition-colors cursor-pointer"
            >
              <i className="ri-youtube-line text-2xl" />
            </a>
          </div>
        </aside>
      </div>

      {/* Floating SNS Panel - Desktop Bottom Right */}
      <div className="hidden lg:flex fixed right-6 bottom-6 z-40 flex-col items-center gap-2">
        <a
          href="https://www.instagram.com/olcompany_inc/"
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-gray-200 hover:bg-white hover:border-gray-300 transition-all cursor-pointer"
          aria-label="Instagram"
        >
          <i className="ri-instagram-line text-lg text-gray-700" />
        </a>
        <a
          href="https://www.youtube.com/@O.L.C"
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-gray-200 hover:bg-white hover:border-gray-300 transition-all cursor-pointer"
          aria-label="YouTube"
        >
          <i className="ri-youtube-line text-lg text-gray-700" />
        </a>
      </div>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#f0efef] border-b border-gray-100 h-14 flex items-center px-5">
        {/* ハンバーガー - 左端 */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="w-8 h-8 flex items-center justify-center text-gray-800 cursor-pointer"
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>

        {/* ロゴ - 中央 */}
        <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex items-center">
          <img
            src="https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/926915bae22d08966848f6c0c2385dc6.webp"
            alt="OL Company"
            className="h-9 w-auto object-contain"
          />
        </Link>

        {/* SNS Icons - Top Right */}
        <div className="ml-auto flex items-center gap-3">
          <a
            href="https://www.instagram.com/olcompany_inc/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-black transition-colors cursor-pointer"
            aria-label="Instagram"
          >
            <i className="ri-instagram-line text-lg" />
          </a>
          <a
            href="https://www.youtube.com/@O.L.C"
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-black transition-colors cursor-pointer"
            aria-label="YouTube"
          >
            <i className="ri-youtube-line text-lg" />
          </a>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-white transition-transform duration-300 ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col pt-20 px-8 gap-0">
          {navItems.map((item) => {
            if (item.external) {
              return (
                <a
                  key={item.en}
                  href={item.external}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col py-4 border-b border-gray-100 cursor-pointer"
                >
                  <span className="text-base font-semibold text-gray-800">{item.en}</span>
                  <span className="text-xs text-gray-400 mt-0.5">{item.ja}</span>
                </a>
              );
            }
            return (
              <Link
                key={item.en}
                to={item.path}
                className="flex flex-col py-4 border-b border-gray-100 cursor-pointer"
              >
                <span className="text-base font-semibold text-gray-800">{item.en}</span>
                <span className="text-xs text-gray-400 mt-0.5">{item.ja}</span>
              </Link>
            );
          })}
          <div className="flex items-center gap-5 mt-8">
            <a href="https://www.instagram.com/olcompany_inc/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-black cursor-pointer">
              <i className="ri-instagram-line text-xl" />
            </a>
            <a href="https://www.youtube.com/@O.L.C" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-black cursor-pointer">
              <i className="ri-youtube-line text-xl" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
